import { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Card, CardContent, TextField, MenuItem, Button,
  Chip, Stack, Divider, List, ListItem, ListItemText, LinearProgress
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import EventIcon from "@mui/icons-material/Event";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import { watchEvents } from "../api/events";
import { db } from "../firebase";
import { ref, get } from "firebase/database";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";

/* ---------- helpers ---------- */
const normLabel = (k) => {
  if (!k) return "";
  const l = String(k).trim().toLowerCase();
  if (l === "behaviour" || l === "behavior") return "Behaviour";
  if (l === "collaboration") return "Collaboration";
  if (l === "reliability") return "Reliability";
  if (l === "initiative") return "Initiative";
  return k;
};
const to3 = (n) => Number(n || 0).toFixed(3);
const fmtDt = (ts) => (ts ? new Date(ts).toLocaleString() : "—");
const monthKey = (ts) => {
  if (!ts) return "unknown";
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; // YYYY-MM
};
const monthLabel = (key) => {
  if (key === "all") return "All months";
  const [y, m] = key.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString(undefined, { month: "long", year: "numeric" });
};

export default function Analytics() {
  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("all");

  useEffect(() => watchEvents((e) => { setEvents(e || {}); setLoading(false); }), []);

  const all = useMemo(() => {
    return Object.values(events || {}).sort(
      (a, b) => (b?.dates?.start || 0) - (a?.dates?.start || 0)
    );
  }, [events]);

  const monthOptions = useMemo(() => {
    const set = new Set(all.map((e) => monthKey(e?.dates?.start)));
    const arr = Array.from(set).filter((k) => k !== "unknown").sort().reverse();
    return ["all", ...arr];
  }, [all]);

  const filtered = useMemo(() => {
    if (month === "all") return all;
    return all.filter((e) => monthKey(e?.dates?.start) === month);
  }, [all, month]);

  const exportEvent = async (eventId) => {
    const evSnap = await get(ref(db, `/events/${eventId}`));
    const ev = evSnap.val();
    if (!ev) return;

    const isQuiz = ev?.type === "QUESTIONNAIRE" && !ev?.peerReview?.enabled;

    // employees (for names/emails)
    const empSnap = await get(ref(db, `/employees`));
    const employees = empSnap.val() || {};
    const infoOf = (uid) => {
      const e = employees?.[uid] || {};
      return {
        name: e?.submittedProfile?.profileFields?.fullName || e?.fullName || String(uid),
        email: e?.submittedProfile?.profileFields?.email || e?.email || "",
      };
    };

    // compile workbook
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("results");

    // header (pretty)
    ws.mergeCells("A1:D1");
    ws.getCell("A1").value = (ev?.title || "Event").toUpperCase();
    ws.getCell("A1").font = { size: 16, bold: true };
    ws.getCell("A1").alignment = { vertical: "middle" };

    ws.getCell("A2").value = "Declared At";
    ws.getCell("B2").value = ev?.results?.declaredAt ? new Date(ev.results.declaredAt).toLocaleString() : "—";

    ws.getCell("A3").value = "Window";
    ws.getCell("B3").value = `${fmtDt(ev?.dates?.start)} → ${fmtDt(ev?.dates?.end)}`;

    ws.addRow([]);
    const addSectionTitle = (text) => {
      const r = ws.addRow([text]);
      ws.mergeCells(`A${r.number}:K${r.number}`);
      r.font = { bold: true };
      r.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F5F5" } };
    };

    if (isQuiz) {
      // ---------- QUIZ EXPORT ----------
      const statusSnap = await get(ref(db, `/eventStatusByUser/${eventId}/standard`));
      const qRaw = statusSnap.val() || {};
      const questions = (ev?.questionnaire?.questions || []).filter((q) => q?.type === "MCQ");

      // Score each participant to a /10, then show percent. (No eligibility concept for quiz here)
      const getCorrectIndex = (q) => {
        const options = q?.options || [];
        if (Number.isInteger(q?.correctAnswerIndex)) return q.correctAnswerIndex;
        if (Number.isInteger(q?.correctAnswer)) return q.correctAnswer;
        if (q?.correctAnswer != null) {
          const i = options.findIndex((o) => o === q.correctAnswer);
          if (i >= 0) return i;
        }
        return null;
      };

      const totalPossible = questions.reduce((acc, q) => acc + (Number(q?.weight) || 0), 0);
      const negative = !!ev?.questionnaire?.negativeMarking;

      const rows = Object.entries(qRaw)
        .filter(([, rec]) => rec?.completed && rec?.answers)
        .map(([uid, rec]) => {
          let gained = 0, penalty = 0;
          questions.forEach((q) => {
            const opts = q?.options || [];
            const cIdx = getCorrectIndex(q);
            const ans = rec?.answers?.[q.id];
            let sIdx = null;
            if (Number.isInteger(ans)) sIdx = ans;
            else {
              const i = opts.findIndex((o) => o === ans);
              if (i >= 0) sIdx = i;
            }
            if (sIdx == null || cIdx == null) return;
            if (sIdx === cIdx) gained += (Number(q?.weight) || 0);
            else if (negative) penalty += (Number(q?.weight) || 0) / 4;
          });
          const raw = gained - penalty;
          const score10 = totalPossible > 0 ? Math.max(0, (raw / totalPossible) * 10) : 0;
          const { name, email } = infoOf(uid);
          return { uid, name, email, total10: score10 };
        })
        .sort((a, b) => (b.total10 - a.total10) || a.name.localeCompare(b.name));

      addSectionTitle("Leaderboard (Quiz)");
      ws.addRow(["Rank", "Employee", "Email", "Total (0–10)"]);
      rows.forEach((r, i) => {
        ws.addRow([i + 1, r.name, r.email, Number(to3(r.total10))]);
      });
    } else {
      // ---------- PEER REVIEW EXPORT (Total out of 15 with Performance Index) ----------
      const perfSnap = await get(ref(db, `/eventStatusByUser/${eventId}/performance`));
      const perf = perfSnap.val() || {};
      const attSnap = await get(ref(db, `/events/${eventId}/attendance`));
      const attendance = attSnap.val() || {};
      const piSnap = await get(ref(db, `/events/${eventId}/performanceIndex`));
      const performanceIndex = piSnap.val() || {};

      // star labels
      const schema = (ev?.peerReview?.schema || ev?.questionnaire?.questions || []).filter((q) => q?.type === "STAR");
      const starLabels = (schema.length
        ? schema.map((q) => normLabel(q?.label || q?.id))
        : ["Behaviour", "Collaboration", "Reliability", "Initiative"]
      ).filter(Boolean);

      // aggregate stars (0–5) and micro (0–1)
      const aggStars = {}; // {target: {label:{sum,count}}}
      const microAgg = {}; // {target: {sum,count}}
      Object.entries(perf).forEach(([, rec]) => {
        const answers = rec?.answers || {};
        const micro = rec?.micro || {};
        Object.entries(answers).forEach(([targetUid, qmap]) => {
          Object.entries(qmap || {}).forEach(([lbl, val]) => {
            if (typeof val !== "number" || !isFinite(val)) return;
            const label = normLabel(lbl);
            aggStars[targetUid] ||= {};
            aggStars[targetUid][label] ||= { sum: 0, count: 0 };
            aggStars[targetUid][label].sum += val;
            aggStars[targetUid][label].count += 1;
          });
        });
        Object.entries(micro).forEach(([targetUid, v]) => {
          const n = Number(v);
          if (!isFinite(n)) return;
          microAgg[targetUid] ||= { sum: 0, count: 0 };
          microAgg[targetUid].sum += n;
          microAgg[targetUid].count += 1;
        });
      });

      const EL_ATTR_MIN = 3.0; // ≥60% of 5
      const EL_LAST5_MIN = 3.0;

      const everyone = Array.from(
        new Set([
          ...Object.keys(aggStars || {}),
          ...Object.keys(microAgg || {}),
          ...Object.keys(attendance || {}),
          ...Object.keys(performanceIndex || {}),
        ])
      );

      const buildRow = (tu) => {
        const { name, email } = infoOf(tu);
        const perQStats = aggStars[tu] || {};
        const perQAvg = {};
        let present = 0, sum = 0;
        starLabels.forEach((lbl) => {
          const st = perQStats[lbl];
          const avg = st && st.count ? st.sum / st.count : 0;
          perQAvg[lbl] = avg;
          if (st && st.count) { present += 1; sum += avg; }
        });

        const attr = present ? sum / present : 0; // 0..5
        const micro = microAgg[tu] && microAgg[tu].count
          ? microAgg[tu].sum / microAgg[tu].count
          : 0; // 0..1
        const att = Math.max(0, Math.min(4, Number(attendance[tu]) || 0)); // 0..4
        const pi = Math.max(0, Math.min(5, Number(performanceIndex[tu]) || 0)); // 0..5

        const failed = starLabels.filter((lbl) => (perQAvg[lbl] || 0) < EL_ATTR_MIN);
        const passAttr = failed.length === 0;
        const passLast5 = (att + micro) >= EL_LAST5_MIN;

        const total15 = attr + micro + att + pi; // 0..15
        const eligible = passAttr && passLast5 && pi >= 3.0; // PI must also be >=60%

        return {
          uid: tu, name, email, perQAvg, attr, micro, att, pi, total15, eligible,
          reason: [
            ...(passAttr ? [] : ["Attributes <3"]),
            ...(passLast5 ? [] : ["Attendance+Micro <3"]),
            ...(pi >= 3.0 ? [] : ["Performance Index <3"]),
          ].join(", ")
        };
      };

      const rows = everyone.map(buildRow);
      const eligibleRows = rows.filter((r) => r.eligible).sort((a, b) => (b.total15 - a.total15) || a.name.localeCompare(b.name));
      const ineligibleRows = rows.filter((r) => !r.eligible).sort((a, b) => a.name.localeCompare(b.name));

      // ---- Eligible section
      addSectionTitle("Eligible (Peer Review)");
      ws.addRow([
        "Rank", "Employee", "Email",
        ...starLabels,
        "Attr ★ (0–5)", "Micro (0–1)", "Attendance (0–4)", "Performance (0–5)", "Total (0–15)"
      ]).font = { bold: true };

      eligibleRows.forEach((r, i) => {
        ws.addRow([
          i + 1,
          r.name,
          r.email,
          ...starLabels.map((lbl) => Number(to3(r.perQAvg?.[lbl] || 0))),
          Number(to3(r.attr)), Number(to3(r.micro)), Number(to3(r.att)), Number(to3(r.pi)),
          Number(to3(r.total15)),
        ]);
      });

      ws.addRow([]);

      // ---- Ineligible section
      addSectionTitle("Ineligible (Peer Review)");
      ws.addRow([
        "Employee", "Email",
        ...starLabels,
        "Attr ★ (0–5)", "Micro (0–1)", "Attendance (0–4)", "Performance (0–5)", "Total (0–15)", "Reason(s)"
      ]).font = { bold: true };

      ineligibleRows.forEach((r) => {
        ws.addRow([
          r.name,
          r.email,
          ...starLabels.map((lbl) => Number(to3(r.perQAvg?.[lbl] || 0))),
          Number(to3(r.attr)), Number(to3(r.micro)), Number(to3(r.att)), Number(to3(r.pi)),
          Number(to3(r.total15)),
          r.reason || "—",
        ]);
      });

      // widths
      const colWidths = [6, 26, 28, ...starLabels.map(() => 16), 16, 14, 16, 18, 14];
      colWidths.forEach((w, i) => (ws.getColumn(i + 1).width = w));
    }

    // borders & number format
    ws.eachRow((row, idx) => {
      row.eachCell((cell) => {
        if (idx >= 5) {
          cell.border = {
            top: { style: "thin", color: { argb: "FFE0E0E0" } },
            left: { style: "thin", color: { argb: "FFE0E0E0" } },
            bottom: { style: "thin", color: { argb: "FFE0E0E0" } },
            right: { style: "thin", color: { argb: "FFE0E0E0" } },
          };
        }
        if (typeof cell.value === "number") {
          cell.numFmt = "0.000";
        }
      });
    });

    const blob = await wb.xlsx.writeBuffer().then((b) => new Blob([b], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const safeTitle = (ev?.title || "event").replace(/[^a-z0-9-_]+/gi, "_");
    saveAs(blob, `${safeTitle}_results.xlsx`);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Analytics & Export
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2} alignItems={{ xs: "stretch", sm: "center" }}>
            <TextField
              select
              label="Month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              {monthOptions.map((m) => (
                <MenuItem key={m} value={m}>{monthLabel(m)}</MenuItem>
              ))}
            </TextField>

            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Filter by month to narrow the list. Export mirrors the Results view (Peer Review totals out of 15 including Performance Index).
            </Typography>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {loading ? (
            <LinearProgress />
          ) : filtered.length === 0 ? (
            <Typography color="text.secondary">No events found for this month.</Typography>
          ) : (
            <List disablePadding>
              {filtered.map((e) => {
                const declared = !!e?.results?.declared;
                const type = e?.type === "QUESTIONNAIRE" && !e?.peerReview?.enabled ? "questionnaire" : "peer review";
                return (
                  <ListItem
                    key={e.id}
                    sx={{
                      px: 0, py: 1.25,
                      display: "flex",
                      alignItems: "flex-start",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                    secondaryAction={
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => exportEvent(e.id)}
                        disabled={!declared}
                        sx={{ fontWeight: 700 }}
                      >
                        Export XLSX
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                          <Typography sx={{ fontWeight: 700 }}>{e.title}</Typography>
                          <Chip
                            size="small"
                            icon={<EventIcon sx={{ fontSize: 16 }} />}
                            label={type}
                            sx={{ ml: .5 }}
                          />
                          <Chip
                            size="small"
                            color={declared ? "success" : "default"}
                            icon={declared ? <CheckCircleIcon /> : <PendingIcon />}
                            label={declared ? "Declared" : "Not declared"}
                          />
                        </Stack>
                      }
                      secondary={
                        <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: .5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Window: {fmtDt(e?.dates?.start)} → {fmtDt(e?.dates?.end)}
                          </Typography>
                          {e?.results?.declaredAt && (
                            <Typography variant="body2" color="text.secondary">
                              Declared at: {fmtDt(e.results.declaredAt)}
                            </Typography>
                          )}
                        </Stack>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
